import React, { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  AdminHeader,
  AdminTitle,
  AdminContent,
  UserTable,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  ActionButton,
  SearchBar,
  FilterContainer,
  PaginationContainer,
  LoadingSpinner,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalActions,
  StatCard,
  StatsContainer,
  FlexContainer,
} from './AdminDashboard.styles';
import EditStallForm from './partials/EditStallForm';

// Define the interface for the stall data with all fields
interface Stall {
  id: string;
  stallNumber: string;
  ownerName: string; // Map to contactPerson for table display
  businessPhone: string;
  isActive: boolean;
  isSuspended: boolean;
  isApproved: boolean; // 👈 NEW FIELD FOR APPROVAL
  createdAt: Date;
  status: 'active' | 'suspended' | 'deactivated' | 'pending'; // 👈 NEW STATUS
  
  // Existing fields...
  actualLocation: string;
  additionalDetails: string;
  businessName: string;
  businessPhotoPreview: string;
  category: string;
  contactPerson: string;
  email: string;
  facebookLink: string;
  googleMapLocation: string;
  instagramLink: string;
  latitude: number;
  linkedinLink: string;
  longitude: number;
  paymentStatus: string;
  selectedCountry: string;
  selectedDistrict: string;
  selectedParish: string;
  selectedRegion: string;
  selectedSubcounty: string;
  selectedVillage: string;
  stallType: string;
  subscriptionAmount: number;
  subscriptionDuration: string;
  tiktokLink: string;
  transactionId: string | null;
  userlisting: string;
  website: string;
  whatsappPhone: string;
  xTwitterLink: string;
  youtubeLink: string;
}

const defaultStall: Stall = {
  id: '',
  stallNumber: '',
  ownerName: '',
  businessPhone: '',
  isActive: false,
  isSuspended: false,
  isApproved: false,
  createdAt: new Date(),
  status: 'pending',
  actualLocation: '',
  additionalDetails: '',
  businessName: '',
  businessPhotoPreview: '',
  category: '',
  contactPerson: '',
  email: '',
  facebookLink: '',
  googleMapLocation: '',
  instagramLink: '',
  latitude: 0,
  linkedinLink: '',
  longitude: 0,
  paymentStatus: 'pending',
  selectedCountry: 'Uganda',
  selectedDistrict: '',
  selectedParish: '',
  selectedRegion: '',
  selectedSubcounty: '',
  selectedVillage: '',
  stallType: 'Standard',
  subscriptionAmount: 0,
  subscriptionDuration: '',
  tiktokLink: '',
  transactionId: null,
  userlisting: '',
  website: '',
  whatsappPhone: '',
  xTwitterLink: '',
  youtubeLink: '',
};

const StallManagement = () => {
  const [stalls, setStalls] = useState<Stall[]>([]);
  const [filteredStalls, setFilteredStalls] = useState<Stall[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStall, setSelectedStall] = useState<Stall | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'activate' | 'suspend' | 'deactivate' | 'delete' | 'edit' | 'approve' | null>(null); // 👈 NEW ACTION TYPE

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    deactivated: 0,
    pending: 0,
  });

  const stallsPerPage = 10;

  const getStallStatus = useCallback((data: any): Stall['status'] => {
      if (!data.isApproved && !data.isActive) {
        return 'pending'; 
      }
      if (data.isSuspended) {
        return 'suspended';
      } 
      if (data.isActive) {
        return 'active';
      }
      return 'deactivated';
  }, []);


  useEffect(() => {
    const stallsCollectionRef = collection(db, 'stalls');
    const unsubscribe = onSnapshot(
      stallsCollectionRef,
      (snapshot) => {
        const fetchedStalls: Stall[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          const status = getStallStatus(data);

          const createdAt = data.createdAt
            ? data.createdAt.toDate
              ? data.createdAt.toDate()
              : new Date(data.createdAt)
            : new Date();

          return {
            id: doc.id,
            stallNumber: data.stallNumber || 'N/A',
            ownerName: data.contactPerson || 'N/A',
            businessPhone: data.businessPhone || 'N/A',
            isActive: data.isActive || false,
            isSuspended: data.isSuspended || false,
            isApproved: data.isApproved || false, 
            createdAt: createdAt,
            status,
            actualLocation: data.actualLocation || '',
            additionalDetails: data.additionalDetails || '',
            businessName: data.businessName || '',
            businessPhotoPreview: data.businessPhotoPreview || '',
            category: data.category || '',
            contactPerson: data.contactPerson || '',
            email: data.email || '',
            facebookLink: data.facebookLink || '',
            googleMapLocation: data.googleMapLocation || '',
            instagramLink: data.instagramLink || '',
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            linkedinLink: data.linkedinLink || '',
            paymentStatus: data.paymentStatus || '',
            selectedCountry: data.selectedCountry || '',
            selectedDistrict: data.selectedDistrict || '',
            selectedParish: data.selectedParish || '',
            selectedRegion: data.selectedRegion || '',
            selectedSubcounty: data.selectedSubcounty || '',
            selectedVillage: data.selectedVillage || '',
            stallType: data.stallType || '',
            subscriptionAmount: data.subscriptionAmount || 0,
            subscriptionDuration: data.subscriptionDuration || '',
            tiktokLink: data.tiktokLink || '',
            transactionId: data.transactionId || null,
            userlisting: data.userlisting || '',
            website: data.website || '',
            whatsappPhone: data.whatsappPhone || '',
            xTwitterLink: data.xTwitterLink || '',
            youtubeLink: data.youtubeLink || '',
          } as Stall;
        });

        setStalls(fetchedStalls);
        updateStats(fetchedStalls);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching stalls:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [getStallStatus]);

  // Existing useEffect for filtering...
  useEffect(() => {
    let result = stalls;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (stall) =>
          stall.stallNumber.toLowerCase().includes(term) ||
          stall.contactPerson.toLowerCase().includes(term) ||
          stall.businessName.toLowerCase().includes(term) ||
          stall.businessPhone.toLowerCase().includes(term) ||
          stall.category.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter((stall) => stall.status === statusFilter);
    }

    setFilteredStalls(result);
    setCurrentPage(1);
  }, [stalls, searchTerm, statusFilter]);

  const updateStats = (stallsList: Stall[]) => {
    const stats = {
      total: stallsList.length,
      active: stallsList.filter((s) => s.status === 'active').length,
      suspended: stallsList.filter((s) => s.status === 'suspended').length,
      deactivated: stallsList.filter((s) => s.status === 'deactivated').length,
      pending: stallsList.filter((s) => s.status === 'pending').length, // 👈 NEW STAT CALCULATION
    };
    setStats(stats);
  };

  // --- NEW: Handle Approve Stall ---
  const handleApproveStall = async () => {
    if (!selectedStall) return;

    setLoading(true);
    const stallDocRef = doc(db, 'stalls', selectedStall.id);

    // Set both isApproved and isActive to true
    const updateData = { isActive: true, isApproved: true, isSuspended: false };

    try {
      await updateDoc(stallDocRef, updateData);
      console.log(`Stall ${selectedStall.stallNumber} approved and activated.`);
    } catch (error) {
      console.error('Error approving stall:', error);
    } finally {
      setLoading(false);
      setShowModal(false);
      setSelectedStall(null);
      setActionType(null);
    }
  };

  // Existing handleStatusChange updated to use isApproved when activating/deactivating
  const handleStatusChange = async (newStatus: 'activate' | 'suspend' | 'deactivate') => {
    if (!selectedStall) return;

    setLoading(true);
    const stallDocRef = doc(db, 'stalls', selectedStall.id);

    let updateData = {};
    if (newStatus === 'activate') {
      // Must also ensure it is approved to be 'active'
      updateData = { isActive: true, isSuspended: false, isApproved: true }; 
    } else if (newStatus === 'suspend') {
      updateData = { isActive: true, isSuspended: true, isApproved: true };
    } else if (newStatus === 'deactivate') {
      // Deactivating an approved stall
      updateData = { isActive: false, isSuspended: false, isApproved: true }; 
    }

    try {
      await updateDoc(stallDocRef, updateData);
      console.log(`Stall ${selectedStall.stallNumber} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating stall status:', error);
    } finally {
      setLoading(false);
      setShowModal(false);
      setSelectedStall(null);
      setActionType(null);
    }
  };

  const handleAddNewStall = () => {
    setSelectedStall(null);
    setActionType('edit');
    setShowModal(true);
  };

  const handleDeleteStall = async () => {
    if (!selectedStall) return;

    setLoading(true);
    const stallDocRef = doc(db, 'stalls', selectedStall.id);

    try {
      await deleteDoc(stallDocRef);
      console.log(`Stall ${selectedStall.stallNumber} deleted successfully.`);
    } catch (error) {
      console.error('Error deleting stall:', error);
    } finally {
      setLoading(false);
      setShowModal(false);
      setSelectedStall(null);
      setActionType(null);
    }
  };

  const openActionModal = (stall: Stall, action: 'activate' | 'suspend' | 'deactivate' | 'delete' | 'edit' | 'approve') => {
    setSelectedStall(stall);
    setActionType(action);
    setShowModal(true);
  };

  const indexOfLastStall = currentPage * stallsPerPage;
  const indexOfFirstStall = indexOfLastStall - stallsPerPage;
  const currentStalls = filteredStalls.slice(indexOfFirstStall, indexOfLastStall);
  const totalPages = Math.ceil(filteredStalls.length / stallsPerPage);

  const getStallStatusText = (stall: Stall) => {
    // Use the derived status field
    switch (stall.status) {
        case 'pending': return 'Pending Approval';
        case 'active': return 'Active';
        case 'suspended': return 'Suspended';
        case 'deactivated': return 'Deactivated';
        default: return 'Unknown';
    }
  };

  const renderModalContent = () => {
    if (!actionType) return null;

    if (actionType === 'edit') {
      return (
        <EditStallForm
          stall={selectedStall || defaultStall}
          onSave={() => {
            setShowModal(false);
            setSelectedStall(null);
            setActionType(null);
          }}
          onCancel={() => {
            setShowModal(false);
            setSelectedStall(null);
            setActionType(null);
          }}
        />
      );
    }

    // For all other actions, a stall must be selected
    if (!selectedStall) return null;

    if (actionType === 'delete') {
      return (
        <ModalContent>
          <ModalHeader>
            <h3>Delete Stall</h3>
          </ModalHeader>
          <p>
            Are you sure you want to **permanently delete** stall **{selectedStall.stallNumber}**? This action cannot be
            undone.
          </p>
          <ModalActions>
            <button onClick={() => setShowModal(false)} className="cancel">
              Cancel
            </button>
            <button onClick={handleDeleteStall} className="ban" disabled={loading}>
              {loading ? 'Deleting...' : 'Confirm Deletion'}
            </button>
          </ModalActions>
        </ModalContent>
      );
    }
    
    // --- NEW: Approve Modal Content ---
    if (actionType === 'approve') {
        return (
            <ModalContent>
            <ModalHeader>
                <h3>Approve Stall</h3>
            </ModalHeader>
            <p>
                Are you sure you want to **approve and activate** stall **{selectedStall.stallNumber}**?
            </p>
            <ModalActions>
                <button
                onClick={() => {
                    setShowModal(false);
                    setSelectedStall(null);
                    setActionType(null);
                }}
                className="cancel"
                >
                Cancel
                </button>
                <button
                onClick={handleApproveStall}
                className="approve"
                disabled={loading}
                >
                {loading ? 'Approving...' : `Confirm Approval`}
                </button>
            </ModalActions>
            </ModalContent>
        );
    }

    // Default status change modal (activate, suspend, deactivate)
    return (
      <ModalContent>
        <ModalHeader>
          <h3>
            {actionType === 'activate' && 'Activate Stall'}
            {actionType === 'suspend' && 'Suspend Stall'}
            {actionType === 'deactivate' && 'Deactivate Stall'}
          </h3>
        </ModalHeader>
        <p>
          Are you sure you want to {actionType} stall **{selectedStall.stallNumber}**?
        </p>
        <ModalActions>
          <button
            onClick={() => {
              setShowModal(false);
              setSelectedStall(null);
              setActionType(null);
            }}
            className="cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => handleStatusChange(actionType as 'activate' | 'suspend' | 'deactivate')}
            className={actionType === 'activate' ? 'approve' : actionType === 'suspend' ? 'suspend' : 'ban'}
            disabled={loading}
          >
            {loading ? 'Processing...' : `Confirm ${actionType}`}
          </button>
        </ModalActions>
      </ModalContent>
    );
  };

  return (
    <>
      <FlexContainer>
        <AdminTitle>Stall Management</AdminTitle>
        <ActionButton onClick={handleAddNewStall} className="approve">
          Add New Stall
        </ActionButton>
      </FlexContainer>

      <StatsContainer>
        <StatCard>
          <h3>Total Stalls</h3>
          <p>{stats.total}</p>
        </StatCard>
        <StatCard className="pending"> {/* Use a distinct class for Pending */}
          <h3>Pending Approval</h3>
          <p>{stats.pending}</p>
        </StatCard>
        <StatCard className="approved">
          <h3>Active</h3>
          <p>{stats.active}</p>
        </StatCard>
        <StatCard className="suspended">
          <h3>Suspended</h3>
          <p>{stats.suspended}</p>
        </StatCard>
        <StatCard className="banned">
          <h3>Deactivated</h3>
          <p>{stats.deactivated}</p>
        </StatCard>
      </StatsContainer>

      <FilterContainer>
        <SearchBar
          type="text"
          placeholder="Search stalls..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending Approval</option> 
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </FilterContainer>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <AdminContent>
     <UserTable>
        <thead>
            <TableRow>
                <TableHeader>Stall Number</TableHeader>
                <TableHeader>Business Name</TableHeader>
                <TableHeader>Contact Person</TableHeader>
                <TableHeader>Phone Number</TableHeader>
                <TableHeader>Category</TableHeader>
                <TableHeader>Created At</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
            </TableRow>
        </thead>
        <tbody>
            {currentStalls.length > 0 ? (
                currentStalls.map((stall) => (
                    <TableRow key={stall.id}>
                        <TableCell>{stall.stallNumber}</TableCell>
                        <TableCell>{stall.businessName}</TableCell>
                        <TableCell>{stall.contactPerson}</TableCell>
                        <TableCell>{stall.businessPhone}</TableCell>
                        <TableCell>{stall.category}</TableCell>
                        <TableCell>{stall.createdAt.toLocaleDateString()}</TableCell>
                        <TableCell><StatusBadge status={stall.status}>{getStallStatusText(stall)}</StatusBadge></TableCell>
                        <TableCell>
                            {/* --- NEW APPROVE BUTTON (Conditional) --- */}
                            {stall.status === 'pending' && (
                                <ActionButton onClick={() => openActionModal(stall, 'approve')} className="approve">Approve</ActionButton>
                            )}
                            
                            {/* Existing action buttons */}
                            {stall.status !== 'pending' && stall.status !== 'active' && (
                                <ActionButton onClick={() => openActionModal(stall, 'activate')} className="approve">Activate</ActionButton>
                            )}
                            {/* {stall.status === 'active' && (
                                <ActionButton onClick={() => openActionModal(stall, 'suspend')} className="suspend">Suspend</ActionButton>
                            )}
                            {(stall.status === 'active' || stall.status === 'suspended') && (
                                <ActionButton onClick={() => openActionModal(stall, 'deactivate')} className="ban">Deactivate</ActionButton>
                            )} */}
                            <ActionButton onClick={() => openActionModal(stall, 'edit')} className="edit">Edit</ActionButton>
                            <ActionButton onClick={() => openActionModal(stall, 'delete')} className="delete">Delete</ActionButton>
                        </TableCell>
                    </TableRow>
                ))
            ) : (
                <TableRow><TableCell colSpan={8} style={{ textAlign: 'center' }}>No stalls found matching your criteria</TableCell></TableRow>
            )}
        </tbody>
    </UserTable>
    
          {totalPages > 1 && (
            <PaginationContainer>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </PaginationContainer>
          )}
        </AdminContent>
      )}

      {showModal && <ModalOverlay>{renderModalContent()}</ModalOverlay>}
    </>
  );
};

export default StallManagement;